import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

const normalizeImage = (item) => ({
  src: item?.image?.publicURL || item?.image || '',
  alt: item?.alt || '',
  caption: item?.caption || '',
})

const BlogPhotoCarousel = ({ carousel }) => {
  const images = useMemo(
    () =>
      (carousel?.images || []).map(normalizeImage).filter((item) => item.src),
    [carousel]
  )
  const [activeIndex, setActiveIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    setActiveIndex(0)
    setIsExpanded(false)
  }, [carousel?.id])

  useEffect(() => {
    if (!isExpanded) {
      return undefined
    }

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setIsExpanded(false)
      }
    }

    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [isExpanded])

  if (images.length === 0) {
    return null
  }

  const activeImage = images[activeIndex]
  const hasMultipleImages = images.length > 1

  const goToPrevious = () => {
    setActiveIndex((index) => (index === 0 ? images.length - 1 : index - 1))
  }

  const goToNext = () => {
    setActiveIndex((index) => (index === images.length - 1 ? 0 : index + 1))
  }

  return (
    <figure
      className="blog-photo-carousel"
      aria-label={carousel?.label || 'Photo carousel'}
    >
      <div className="blog-photo-carousel-stage">
        <button
          className="blog-photo-carousel-expand"
          type="button"
          aria-label="Expand photo"
          onClick={() => setIsExpanded(true)}
        >
          <img src={activeImage.src} alt={activeImage.alt} />
          <span className="blog-photo-carousel-expand-hint">Expand</span>
        </button>
        {hasMultipleImages ? (
          <>
            <button
              className="blog-photo-carousel-button blog-photo-carousel-button-previous"
              type="button"
              aria-label="Previous photo"
              onClick={goToPrevious}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                <path d="M15.5 19 8.5 12l7-7" />
              </svg>
            </button>
            <button
              className="blog-photo-carousel-button blog-photo-carousel-button-next"
              type="button"
              aria-label="Next photo"
              onClick={goToNext}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                <path d="m8.5 5 7 7-7 7" />
              </svg>
            </button>
          </>
        ) : null}
      </div>
      <figcaption className="blog-photo-carousel-caption">
        {activeImage.caption ? <span>{activeImage.caption}</span> : null}
        {hasMultipleImages ? (
          <span className="blog-photo-carousel-count">
            {activeIndex + 1} of {images.length}
          </span>
        ) : null}
      </figcaption>
      {hasMultipleImages ? (
        <div
          className="blog-photo-carousel-thumbnails"
          aria-label="Choose photo"
        >
          {images.map((image, index) => (
            <button
              key={`${image.src}-${index}`}
              className={`blog-photo-carousel-thumbnail${
                index === activeIndex ? ' is-active' : ''
              }`}
              type="button"
              aria-label={`Show photo ${index + 1}`}
              aria-current={index === activeIndex ? 'true' : undefined}
              onClick={() => setActiveIndex(index)}
            >
              <img src={image.src} alt={image.alt || `Photo ${index + 1} thumbnail`} />
            </button>
          ))}
        </div>
      ) : null}
      {isExpanded ? (
        <div
          className="blog-photo-carousel-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={carousel?.label || 'Expanded photo'}
        >
          <button
            className="blog-photo-carousel-lightbox-backdrop"
            type="button"
            aria-label="Close expanded photo"
            onClick={() => setIsExpanded(false)}
          />
          <div className="blog-photo-carousel-lightbox-content">
            <button
              className="blog-photo-carousel-lightbox-close"
              type="button"
              aria-label="Close expanded photo"
              onClick={() => setIsExpanded(false)}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
            <img src={activeImage.src} alt={activeImage.alt} />
            {hasMultipleImages ? (
              <>
                <button
                  className="blog-photo-carousel-button blog-photo-carousel-lightbox-previous"
                  type="button"
                  aria-label="Previous expanded photo"
                  onClick={goToPrevious}
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                    <path d="M15.5 19 8.5 12l7-7" />
                  </svg>
                </button>
                <button
                  className="blog-photo-carousel-button blog-photo-carousel-lightbox-next"
                  type="button"
                  aria-label="Next expanded photo"
                  onClick={goToNext}
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                    <path d="m8.5 5 7 7-7 7" />
                  </svg>
                </button>
              </>
            ) : null}
            <p className="blog-photo-carousel-lightbox-caption">
              {activeImage.caption}
              {hasMultipleImages ? (
                <span>
                  {activeIndex + 1} of {images.length}
                </span>
              ) : null}
            </p>
          </div>
        </div>
      ) : null}
    </figure>
  )
}

BlogPhotoCarousel.propTypes = {
  carousel: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            publicURL: PropTypes.string,
          }),
        ]),
        alt: PropTypes.string,
        caption: PropTypes.string,
      })
    ),
  }),
}

export default BlogPhotoCarousel
